import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import findMenuNestedItems from 'src/utils/findMenuNestedItems';

import { Button } from 'src/components/basicContent/_Button/Button';

import ChevronDownIconSvg from 'public/icons/ChevronDown.svg';

import { NavigationMenu, NavigationMenuItems } from '../NavigationMenu';

import SubMenu from './SubMenu/SubMenu';

import styles from './_MultiTierMenu.module.css';

type MultiLevelMenuProps = {
	NavigationMenu?: NavigationMenu;
	className?: string;
	setSelectedMenuItemTitle?: (value: Field<string>) => void;
	setShowSubMenu?: (value: boolean) => void;
};

export default function MultiLevelMenu(props: MultiLevelMenuProps): JSX.Element {
	const router = useRouter();

	const menuWrapperRef = useRef<HTMLDivElement | null>(null);
	const subMenuRef = useRef<HTMLDivElement | null>(null);
	const menuItemRefs = useRef<(HTMLLIElement | null)[]>([]);
	const menuButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

	const [overlayLeft, setOverlayLeft] = useState(0);

	const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
	const [submenuPos, setSubmenuPos] = useState<{ left: number | string }>({
		left: 'auto',
	});

	const calculateSubMenuPosition = (buttonElement: HTMLElement) => {
		const buttonRect = buttonElement.getBoundingClientRect();
		const clickedLi = buttonElement.closest('li');

		const menuWrapperRect = menuWrapperRef.current?.getBoundingClientRect();
		const subMenuWrapperRect = subMenuRef.current?.getBoundingClientRect();

		if (!menuWrapperRect || !subMenuWrapperRect) {
			return;
		}

		const isLastItem =
			clickedLi === menuItemRefs.current[menuItemRefs.current.length - 1];

		const isCompactLayout = menuWrapperRect.width <= 1250;
		const shouldBeRight =
			buttonRect.right + subMenuWrapperRect.width > menuWrapperRect.width;

		const leftOffset = -(buttonRect.left - 35);
		const topAdjustedOffset = (buttonRect.left - menuWrapperRect.right) - 35;
		const desktopOffset =
			-((buttonRect.left - menuWrapperRect.left) - 150);

		let submenuLeft: number;

		if (shouldBeRight) {
			submenuLeft = isCompactLayout
				? isLastItem
					? topAdjustedOffset
					: leftOffset
				: desktopOffset;
		} else {
			submenuLeft = isCompactLayout
				? leftOffset
				: -menuWrapperRect.left;
		}

		setSubmenuPos({ left: submenuLeft });
	};

	useEffect(() => {
		if (!activeSubMenu) {
			return;
		}

		const activeIndex = props.NavigationMenu?.items?.findIndex(
			item => item.Title.value === activeSubMenu
		);

		const button = menuButtonRefs.current[activeIndex ?? -1];

		if (button) {
			calculateSubMenuPosition(button);
		}
	}, [activeSubMenu, props.NavigationMenu?.items]);
	
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;

			if (
				(menuWrapperRef?.current?.contains(target) && subMenuRef?.current?.contains(target)) ||
				(!menuWrapperRef?.current?.contains(target) && subMenuRef?.current?.contains(target))
			) {
				return;
			}

			setActiveSubMenu(null);
			document.querySelector('html')?.classList.remove('no-scrolling');
		};

		window.addEventListener('click', handleClickOutside);

		return () => {
			window.removeEventListener('click', handleClickOutside);
		};
	}, [activeSubMenu]);

	const calculateOverlayDimensions = () => {
		const rect = menuWrapperRef.current?.getBoundingClientRect();
		if (!rect) {
			setActiveSubMenu(null);
			return;
		}

		setOverlayLeft(-rect.left);
		document.querySelector('html')?.classList.add('no-scrolling');
	};

	const menuButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement>, isOpen: boolean, item: NavigationMenuItems) => {
		e.stopPropagation();

		calculateSubMenuPosition(e.currentTarget);

		setActiveSubMenu?.(isOpen ? null : item.Title.value);
		props.setSelectedMenuItemTitle?.(item.Title);
		document.querySelector('html')?.classList.add('no-scrolling');
		calculateOverlayDimensions?.();
		props.setShowSubMenu?.(true);
	};

	return (
		<div className={`${styles.menuContainer} ${props.className} maxWidth`} ref={menuWrapperRef}>
			<ul className={styles.menuList}>
				{props.NavigationMenu?.items?.map((item, index) => {
					const nestedItems = findMenuNestedItems(item, router.asPath);
					const isOpen = activeSubMenu === item.Title.value;

					const classNames = [
						activeSubMenu === item.Title.value ? styles.subMenuActive : '',
						nestedItems.active || router.asPath.split('?')[0].toLowerCase() === item.Link?.value?.href ? styles.active : '',
						isOpen ? styles.showSubMenu : '',
					].join(' ');

					return (
						<li ref={(el) => (menuItemRefs.current[index] = el)} key={`headerlink-${index}`} className={classNames}>
							{item.items ? (
								<>
									<Button
										buttonRef={(el) => {
												menuButtonRefs.current[index] = el;
											}}
										title="Show Sub Menu"
										className={styles.secondaryLinksParent}
										onClick={(e) => menuButtonClickHandler(e, isOpen, item)}
									>
										<ChevronDownIconSvg className={styles.chevron} />
										<Text tag="span" field={item.Title} className={styles.secondaryLinkText} />
									</Button>

									{isOpen && (
										<SubMenu
											subMenuRef={subMenuRef}
											subMenuItems={item}
											submenuPos={submenuPos}
											activeSubMenu={activeSubMenu}
											nestedItem={nestedItems.nestedItems}
											showTextBlocks
											showNavigationLinks
											showHighlights
											setActiveSubMenu={(value) => setActiveSubMenu(value)}
										/>
									)}
								</>
							) : (
								<Link href={item.Link?.value?.href || ''}>
									<a
										target={item.Link?.value?.target}
										onClick={() => {
											setActiveSubMenu(null);
											document.documentElement.classList.remove('no-scrolling');
										}}
									>
										{item.Link?.value?.title}
									</a>
								</Link>
							)}
						</li>
					);
				})}
			</ul>
			<div className={`${activeSubMenu ? styles.overlay : ''}`} style={{ left: overlayLeft }} />
		</div>
	);
}
